// `grid vault list` — #1766. Lists the grid's vault items: key, type, scope,
// last-resolved, and active-grant count. Values are never returned by the
// list endpoint, so they can never be printed.

import { Command } from 'commander';
import { getV2Client, type IV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { Output } from '../../output.js';
import { addOutputOption, isStructuredOutput } from '../../services/output-format.js';
import { addGridOption, gridSlug, type GridOpts } from '../../grid-opt.js';
import {
  announceGridTarget,
  resolveVaultGrid,
  vaultItemsPath,
  type VaultItemWire,
} from './shared.js';

export interface RunVaultListOpts {
  client: IV2Client;
  gridOverride: string | undefined;
  scope?: string;
  json?: boolean;
  output?: string;
}

export async function runVaultList(opts: RunVaultListOpts): Promise<void> {
  const grid = await resolveVaultGrid(opts.client, opts.gridOverride);
  announceGridTarget(grid);

  const resp = await opts.client.get<{ items?: VaultItemWire[] }>(
    vaultItemsPath(grid.gridId),
    opts.scope ? { scope: opts.scope } : undefined,
  );
  const items = Array.isArray(resp?.items) ? resp.items : [];

  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });
  if (out.isStructured) {
    out.json(() => items);
    return;
  }

  if (items.length === 0) {
    process.stdout.write('No vault items in this grid.\n');
    return;
  }

  process.stdout.write(renderTable(items));
}

function renderTable(items: VaultItemWire[]): string {
  const header = ['KEY', 'TYPE', 'SCOPE', 'LAST RESOLVED', 'GRANTS'];
  const rows = items.map((it) => [
    it.key,
    it.type,
    it.scope,
    it.last_resolved_at ?? 'never',
    String(it.grants_count ?? 0),
  ]);
  const widths = header.map((h, i) =>
    Math.max(h.length, ...rows.map((r) => r[i].length)),
  );
  const fmt = (cols: string[]) =>
    cols.map((c, i) => c.padEnd(widths[i])).join('  ').replace(/\s+$/, '') + '\n';
  return [fmt(header), ...rows.map(fmt)].join('');
}

export const vaultListCommand: Command = addOutputOption(
  addGridOption(
    new Command('list').description('List vault items in a grid (values are never shown)'),
    'Grid to list (defaults to the active grid)',
  ).option('--scope <scope>', 'Filter by scope: org | personal')
    .option('--json', 'JSON output (alias of -o json)'),
).action(async (opts: GridOpts & { scope?: string; json?: boolean; output?: string }) => {
  try {
    const gridOverride = gridSlug(opts);
    const client = await getV2Client(gridOverride ?? undefined);
    await runVaultList({
      client,
      gridOverride,
      scope: opts.scope,
      json: opts.json,
      output: opts.output,
    });
  } catch (err) {
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});
